import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, Download, BarChart3, Receipt, Package, CreditCard, Wallet, Pencil, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getSessionToken } from "@/utils/auth";
import { PeriodPicker, periodFor } from "@/components/RevenuePeriodPicker";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

// ---------------------------------------------------------------------------
// Memorial Day — "Text the Holdouts" one-tap blast
// ---------------------------------------------------------------------------
function HoldoutsButton() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { count, preview: [...] } when confirming
  const [sending, setSending] = useState(false);

  const openConfirm = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/reports/memorial-day/holdouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!data.count) {
        toast.info("No holdouts to text — every Memorial Day lead has booked or already received the final SMS.");
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error("Couldn't load holdouts list");
    } finally {
      setLoading(false);
    }
  };

  const sendBlast = async () => {
    setSending(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.post(`${API}/reports/memorial-day/text-holdouts`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const parts = [`${data.sent} sent`];
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`Memorial Day holdouts: ${parts.join(", ")}`);
      setPreview(null);
    } catch (e) {
      toast.error("Failed to send holdout SMS blast");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={openConfirm}
        disabled={loading}
        data-testid="md-text-holdouts-button"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#b22234] text-white hover:bg-[#9a1d2d] disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Text the Holdouts
      </button>

      {preview && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => !sending && setPreview(null)}
          data-testid="md-holdouts-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b bg-gradient-to-r from-red-50 via-white to-blue-50">
              <h3 className="font-bold text-zinc-900">🇺🇸 Text {preview.count} Memorial Day holdout{preview.count === 1 ? '' : 's'}?</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Sends "campaign ends in 6 hours" SMS only to leads who qualified for a tier and haven't booked yet. Each lead receives this at most once.
              </p>
            </div>
            <div className="max-h-60 overflow-y-auto divide-y" data-testid="md-holdouts-preview-list">
              {preview.preview.map((p) => (
                <div key={p.lead_id} className="px-4 py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-zinc-900">{p.name}</div>
                    <div className="text-xs text-zinc-400">{p.phone} • tier {p.tier}</div>
                  </div>
                  <span className="text-[10px] text-zinc-500 uppercase">{p.estimate_status || "new"}</span>
                </div>
              ))}
              {preview.count > preview.preview.length && (
                <div className="px-4 py-2 text-xs text-zinc-400 text-center">
                  …and {preview.count - preview.preview.length} more
                </div>
              )}
            </div>
            <div className="p-3 border-t flex gap-2 justify-end">
              <button
                onClick={() => setPreview(null)}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg"
                data-testid="md-holdouts-cancel"
              >
                Cancel
              </button>
              <button
                onClick={sendBlast}
                disabled={sending}
                data-testid="md-holdouts-confirm-send"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-[#b22234] text-white hover:bg-[#9a1d2d] disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to {preview.count}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProfitCard({ data, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(data.monthly_overhead || ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(data.monthly_overhead || ""));
    setEditing(false);
  }, [data.monthly_overhead, data.month]);

  const save = async () => {
    setSaving(true);
    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/monthly-overhead`,
        { monthly_overhead: parseFloat(value) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Monthly overhead updated");
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="profit-card">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-indigo-600" />
          Profit
        </h2>
        <span className="text-xs text-zinc-400">Net − Overhead</span>
      </div>
      <div className="grid grid-cols-2 gap-px bg-zinc-100">
        <div className="bg-white p-4">
          <div className="text-xs text-zinc-500 font-medium uppercase tracking-wide flex items-center justify-between">
            <span>Monthly Overhead</span>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                data-testid="edit-overhead-button"
                className="text-zinc-400 hover:text-zinc-700"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {!editing ? (
            <>
              <div className="text-xl font-bold text-zinc-900 mt-1">{fmt(data.monthly_overhead)}</div>
              <div className="text-[11px] text-zinc-400 mt-0.5" data-testid="period-overhead-note">
                {data.period_months > 1
                  ? `× ${data.period_months} months = ${fmt(data.period_overhead)} this period`
                  : "Gas, insurance, phone, etc."}
              </div>
            </>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  autoFocus
                  data-testid="overhead-input"
                  className="w-full h-9 rounded border-2 border-indigo-300 pl-5 pr-2 text-sm focus:border-indigo-500 outline-none"
                />
              </div>
              <button
                onClick={save}
                disabled={saving}
                className="h-9 px-2 rounded bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
                data-testid="save-overhead-button"
              >
                {saving ? "…" : "Save"}
              </button>
              <button
                onClick={() => { setEditing(false); setValue(String(data.monthly_overhead || "")); }}
                className="h-9 px-2 rounded border border-zinc-300 text-xs text-zinc-600"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <div className="bg-white p-4">
          <div className="text-xs text-indigo-600 font-medium uppercase tracking-wide">Profit</div>
          <div className={`text-xl font-bold mt-1 ${data.profit >= 0 ? "text-indigo-700" : "text-red-600"}`}>
            {fmt(data.profit)}
          </div>
          <div className={`text-[11px] mt-0.5 ${data.profit_margin_pct >= 0 ? "text-zinc-500" : "text-red-600"}`}>
            {data.total_revenue > 0 ? `${data.profit_margin_pct}% margin` : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodsCard({ methods, total }) {
  if (!methods?.length) return null;
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="payment-methods-card">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-blue-500" /> By Payment Method
        </h2>
        <span className="text-xs text-zinc-400">{fmt(total)} collected</span>
      </div>
      <div className="divide-y">
        {methods.map((pm) => (
          <div key={pm.method} className="px-4 py-3" data-testid={`payment-method-${pm.method.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium text-zinc-900">{pm.method}</div>
                <div className="text-xs text-zinc-400">{pm.count} invoice{pm.count === 1 ? "" : "s"} · {pm.percent}%</div>
              </div>
              <div className="font-semibold text-zinc-900">{fmt(pm.amount)}</div>
            </div>
            <div className="mt-2 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pm.percent}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RevenueBreakdown() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("month");
  const [offset, setOffset] = useState(0);
  const period = periodFor(mode, offset);
  const periodKey = JSON.stringify(period.params);
  const isMonth = mode === "month";

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/reports/revenue-breakdown`, {
        headers: { Authorization: `Bearer ${token}` },
        params: period.params,
      });
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadTaxCsv = async () => {
    try {
      const endIncl = new Date(`${data.period_end}T12:00:00`);
      endIncl.setDate(endIncl.getDate() - 1);
      const { data: rep } = await axios.get(`${API}/reports/tax`, {
        headers: { Authorization: `Bearer ${getSessionToken()}` },
        params: { start_date: data.period_start, end_date: endIncl.toISOString().slice(0, 10) },
      });
      const taxed = (rep.invoices || []).filter((i) => (i.tax || 0) > 0);
      if (!taxed.length) { toast.error("No taxed invoices in this period"); return; }
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const rows = [["Invoice #", "Customer", "Total", "Taxable", "Tax", "Paid Date"],
        ...taxed.map((i) => [i.invoice_number, i.customer_name, (+i.total || 0).toFixed(2), (+i.taxable_subtotal || 0).toFixed(2), (+i.tax || 0).toFixed(2), i.paid_at ? new Date(i.paid_at).toLocaleDateString() : ""]),
        [],
        ["TOTALS", "", taxed.reduce((s, i) => s + (i.total || 0), 0).toFixed(2), taxed.reduce((s, i) => s + (i.taxable_subtotal || 0), 0).toFixed(2), taxed.reduce((s, i) => s + (i.tax || 0), 0).toFixed(2), ""]];
      const blob = new Blob([rows.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `taxed-invoices-${data.period_start}-to-${endIncl.toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${taxed.length} taxed invoice${taxed.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Couldn't build the tax CSV");
    }
  };

  // Color palette for product bars
  const COLORS = [
    "bg-[#207024]", "bg-blue-500", "bg-amber-500", "bg-purple-500",
    "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-indigo-500"
  ];
  const TEXT_COLORS = [
    "text-[#207024]", "text-blue-500", "text-amber-500", "text-purple-500",
    "text-rose-500", "text-cyan-500", "text-orange-500", "text-indigo-500"
  ];

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/settings")} className="p-2 -ml-2 hover:bg-zinc-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-barlow text-xl font-bold uppercase">Revenue Breakdown</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <PeriodPicker
          mode={mode}
          offset={offset}
          label={period.label || data?.month_label || "…"}
          onMode={(m) => { setMode(m); setOffset(0); }}
          onOffset={setOffset}
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
          </div>
        ) : data ? (
          <>
            {/* Top KPIs */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-[#207024]" />
                  <span className="text-xs text-zinc-500 font-medium">Total Revenue</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">{fmt(data.total_revenue)}</div>
                {data.revenue_change_percent !== 0 && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${data.revenue_change_percent > 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {data.revenue_change_percent > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {data.revenue_change_percent > 0 ? '+' : ''}{data.revenue_change_percent}% vs {isMonth ? "last month" : "previous period"}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Receipt className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-zinc-500 font-medium">Invoices Paid</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">{data.total_invoices}</div>
                <div className="text-xs text-zinc-400 mt-1">Avg {fmt(data.avg_invoice_value)}/invoice</div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-zinc-500 font-medium">Total Units</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">{data.total_units}</div>
                <div className="text-xs text-zinc-400 mt-1">Avg {fmt(data.avg_unit_price)}/unit</div>
              </div>
            </div>

            {/* Profitability — Tax / Labor / Material / Net */}
            <div className="bg-white rounded-xl border overflow-hidden" data-testid="profitability-section">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Profitability
                </h2>
                <span className="text-xs text-zinc-400">Revenue − Tax − Labor − Material</span>
              </div>
              <div className="grid grid-cols-2 gap-px bg-zinc-100">
                <div className="bg-white p-4">
                  <div className="text-xs text-purple-500 font-medium uppercase tracking-wide">Tax Collected</div>
                  <div className="text-xl font-bold text-zinc-900 mt-1">{fmt(data.total_tax)}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">Remitted to state</div>
                </div>
                <div className="bg-white p-4">
                  <div className="text-xs text-rose-500 font-medium uppercase tracking-wide">Labor Cost</div>
                  <div className="text-xl font-bold text-zinc-900 mt-1">{fmt(data.total_labor_cost)}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    {data.total_revenue > 0 ? `${((data.total_labor_cost / data.total_revenue) * 100).toFixed(1)}% of rev` : "—"}
                  </div>
                </div>
                <div className="bg-white p-4">
                  <div className="text-xs text-amber-600 font-medium uppercase tracking-wide">Material Cost</div>
                  <div className="text-xl font-bold text-zinc-900 mt-1">{fmt(data.total_material_cost)}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    {data.total_revenue > 0 ? `${((data.total_material_cost / data.total_revenue) * 100).toFixed(1)}% of rev` : "—"}
                  </div>
                  {data.materials_eligible_count > 0 && (
                    <div className="text-[11px] text-amber-700 mt-0.5" data-testid="materials-reconciled-count">
                      {data.materials_reconciled_count} of {data.materials_eligible_count} paid invoices reconciled
                    </div>
                  )}
                </div>
                {data.contractor_invoices > 0 && (
                  <div className="bg-white p-4">
                    <div className="text-xs text-orange-600 font-medium uppercase tracking-wide">Contractor Payouts</div>
                    <div className="text-xl font-bold text-zinc-900 mt-1">{fmt(data.total_contractor_payout || 0)}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      {data.contractor_invoices} contractor {data.contractor_invoices === 1 ? "invoice" : "invoices"}
                    </div>
                  </div>
                )}
                <div className="bg-white p-4">
                  <div className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Net Revenue</div>
                  <div className={`text-xl font-bold mt-1 ${data.net_revenue >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {fmt(data.net_revenue)}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${data.net_margin_pct >= 0 ? "text-sky-700" : "text-red-600"}`}>
                    {data.total_revenue > 0 ? `${data.net_margin_pct}% margin` : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Profit (after Monthly Overhead) */}
            <ProfitCard data={data} onUpdated={() => fetchData()} />

            <PaymentMethodsCard methods={data.payment_methods} total={data.total_revenue} />

            <button onClick={downloadTaxCsv}
              className="w-full h-11 rounded-xl border border-zinc-300 bg-white text-sm font-semibold text-zinc-800 flex items-center justify-center gap-2 hover:bg-zinc-50"
              data-testid="download-tax-csv">
              <Download className="w-4 h-4" /> Download taxed invoices (CSV) · {fmt(data.total_tax)} tax
            </button>

            {/* Revenue by Product */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#207024]" />
                  Revenue by Service
                </h2>
              </div>

              {data.products.length === 0 ? (
                <div className="p-8 text-center text-zinc-400">No paid invoices in this period</div>
              ) : (
                <div className="divide-y">
                  {data.products.map((product, idx) => (
                    <div key={product.name} className="p-4" data-testid={`product-${idx}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-semibold text-zinc-900">{product.name}</div>
                          <div className="text-sm text-zinc-500">
                            {product.total_units} units @ {fmt(product.avg_price)} avg
                            {product.taxable && <span className="text-zinc-400 ml-1">(taxable)</span>}
                          </div>
                          {product.total_material_cost > 0 && (
                            <div className="text-xs text-amber-600 mt-0.5">
                              Material: {fmt(product.total_material_cost)} ({fmt(product.unit_material_cost)}/unit)
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className={`font-bold ${TEXT_COLORS[idx % TEXT_COLORS.length]}`}>
                            {fmt(product.total_revenue)}
                          </div>
                          <div className="text-sm text-zinc-500">{product.revenue_percent}% of revenue</div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full bg-zinc-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${COLORS[idx % COLORS.length]}`}
                          style={{ width: `${product.revenue_percent}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Units Breakdown - horizontal bar chart style */}
            {data.products.length > 0 && (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-4 border-b">
                  <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-500" />
                    Units Breakdown
                  </h2>
                </div>
                <div className="p-4 space-y-3">
                  {data.products.map((product, idx) => (
                    <div key={product.name} className="flex items-center gap-3">
                      <div className="w-32 text-sm text-zinc-700 truncate" title={product.name}>
                        {product.name}
                      </div>
                      <div className="flex-1 bg-zinc-100 rounded-full h-6 relative overflow-hidden">
                        <div
                          className={`h-6 rounded-full ${COLORS[idx % COLORS.length]} flex items-center justify-end pr-2`}
                          style={{ width: `${Math.max(product.units_percent, 8)}%` }}
                        >
                          <span className="text-xs text-white font-bold">{product.total_units}</span>
                        </div>
                      </div>
                      <div className="w-12 text-right text-sm text-zinc-500">{product.units_percent}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Memorial Day Sale roll-up — only renders when at least one
                paid invoice this month came from the campaign */}
            {data.memorial_day?.active && (
              <div
                className="bg-white rounded-xl border-2 border-[#b22234]/30 overflow-hidden"
                data-testid="md-revenue-card"
              >
                <div className="p-4 border-b bg-gradient-to-r from-red-50 via-white to-blue-50">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                        <span className="text-base">🇺🇸</span>
                        Memorial Day Sale
                      </h2>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {data.memorial_day.total_invoices > 0 ? (
                          <>
                            {data.memorial_day.total_invoices} paid invoice{data.memorial_day.total_invoices === 1 ? '' : 's'} • Customers saved {fmt(data.memorial_day.total_savings)}
                          </>
                        ) : (
                          <>
                            {data.memorial_day.qualified_leads_total || 0} qualified lead{(data.memorial_day.qualified_leads_total || 0) === 1 ? '' : 's'} in pipeline • No sales closed yet
                          </>
                        )}
                      </p>
                    </div>
                    <HoldoutsButton />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-px bg-zinc-100">
                  <div className="bg-white p-4">
                    <div className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Campaign Revenue</div>
                    <div className="text-xl font-bold text-zinc-900 mt-1" data-testid="md-campaign-revenue">
                      {fmt(data.memorial_day.total_revenue)}
                    </div>
                  </div>
                  <div className="bg-white p-4">
                    <div className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Total Savings Given</div>
                    <div className="text-xl font-bold text-[#b22234] mt-1" data-testid="md-total-savings">
                      {fmt(data.memorial_day.total_savings)}
                    </div>
                  </div>
                </div>
                <div className="divide-y">
                  {["1", "2", "3"].map((tierKey) => {
                    const t = data.memorial_day.tiers[tierKey];
                    if (!t || t.invoices === 0) return null;
                    return (
                      <div
                        key={tierKey}
                        className="px-4 py-3 flex justify-between items-center"
                        data-testid={`md-tier-${tierKey}-row`}
                      >
                        <div>
                          <div className="font-medium text-zinc-900">{t.label}</div>
                          <div className="text-xs text-zinc-400">
                            {t.invoices} invoice{t.invoices === 1 ? '' : 's'} • {fmt(t.savings)} in savings
                          </div>
                        </div>
                        <div className="font-semibold text-zinc-900">{fmt(t.revenue)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Month over Month Comparison */}
            <div className="bg-gradient-to-r from-zinc-800 to-zinc-700 rounded-xl p-4 text-white">
              <h3 className="text-sm text-zinc-300 mb-3">{isMonth ? "Month-over-Month" : "vs Previous Period"}</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-zinc-400">{isMonth ? "Last Month" : "Previous"}</div>
                  <div className="text-lg font-bold">{fmt(data.prev_month_revenue)}</div>
                  <div className="text-xs text-zinc-400">{data.prev_month_invoices} invoices</div>
                </div>
                <div className="flex items-center justify-center">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                    data.revenue_change_percent > 0 ? 'bg-green-500/20 text-green-400' :
                    data.revenue_change_percent < 0 ? 'bg-red-500/20 text-red-400' : 'bg-zinc-600 text-zinc-300'
                  }`}>
                    {data.revenue_change_percent > 0 ? '+' : ''}{data.revenue_change_percent}%
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-400">{isMonth ? "This Month" : "This Period"}</div>
                  <div className="text-lg font-bold">{fmt(data.total_revenue)}</div>
                  <div className="text-xs text-zinc-400">{data.total_invoices} invoices</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-20 text-zinc-400">No data available</div>
        )}
      </div>
    </div>
  );
}
